import React from "react";
import { FlowStep, VehicleSide, VehicleSurveyImage } from "../../interfaces/altair";
import "./VehicleSurveyOverlay.scss";

const SIDE_ORDER: VehicleSide[] = ["Chassis", "Front", "Right", "Back", "Left"];

const SIDE_LABELS: Record<VehicleSide, string> = {
  Chassis: "شاسيه",
  Front: "أمامي",
  Right: "يمين",
  Back: "خلفي",
  Left: "يسار",
};

interface VehicleSurveyOverlayProps {
  visible: boolean;
  flowStep: FlowStep;
  surveyImages: VehicleSurveyImage[];
  claimSurveyImages: VehicleSurveyImage[];
  nextSideToCapture?: VehicleSide | null;
  onCaptureClick?: () => void | Promise<void>;
  captureInProgress?: boolean;
  isValidating?: boolean;
  validationError?: string | null;
}

type StepState = "completed" | "active" | "upcoming";

function getStepState(
  side: VehicleSide,
  capturedSides: Set<string>,
  nextSide: VehicleSide | null
): StepState {
  if (capturedSides.has(side)) return "completed";
  if (side === nextSide) return "active";
  return "upcoming";
}

const CheckIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
    <path
      d="M8.5 13.5L5 10l1.06-1.06L8.5 11.38l5.44-5.44L15 7l-6.5 6.5z"
      fill="#0049E6"
    />
  </svg>
);

const CircleIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
    <circle cx="10" cy="10" r="7" stroke="#ABADAF" strokeWidth="1.5" />
  </svg>
);

export const VehicleSurveyOverlay: React.FC<VehicleSurveyOverlayProps> = ({
  visible,
  flowStep,
  surveyImages,
  claimSurveyImages,
  nextSideToCapture = null,
  onCaptureClick,
  captureInProgress = false,
  isValidating = false,
  validationError = null,
}) => {
  if (!visible) return null;

  const isClaimFlow =
    flowStep === "claim_vehicle_survey" || claimSurveyImages.length > 0;
  const currentImages = isClaimFlow ? claimSurveyImages : surveyImages;
  const capturedSides = new Set(currentImages.map((img) => img.side));
  const totalImages = currentImages.length;
  const canCapture =
    nextSideToCapture != null &&
    onCaptureClick != null &&
    !captureInProgress &&
    !isValidating;
  const allDone = totalImages >= 5;

  return (
    <div className="survey-panel" dir="rtl">
      {/* Header */}
      <div className="survey-panel__header">
        <div className="survey-panel__live-dot" />
        <span className="survey-panel__title">معاينة المركبة</span>
        <span className="survey-panel__counter">{totalImages}/5</span>
      </div>

      {/* Body: car image + checklist */}
      <div className="survey-panel__body">
        {/* Car image area */}
        <div className="survey-panel__car-area">
          <img
            src="/CarImage.png"
            alt="Car"
            className="survey-panel__car-img"
          />
          {/* Capture button over the car image */}
          {onCaptureClick != null && (
            <div className="survey-panel__capture-zone">
              {(allDone && !isValidating && !captureInProgress) ? (
                <div className="survey-panel__capture-done">اكتملت الصور</div>
              ) : (
                <button
                  type="button"
                  className={`survey-panel__capture-btn ${
                    !canCapture ? "survey-panel__capture-btn--disabled" : ""
                  }`}
                  onClick={() => onCaptureClick()}
                  disabled={!canCapture}
                >
                  {isValidating
                    ? "جاري التحقق..."
                    : captureInProgress
                    ? "جاري التقاط..."
                    : nextSideToCapture != null
                    ? `التقاط (${SIDE_LABELS[nextSideToCapture]})`
                    : "التقاط"}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Step checklist */}
        <div className="survey-panel__steps">
          {SIDE_ORDER.map((side) => {
            const state = getStepState(side, capturedSides, nextSideToCapture);
            return (
              <div
                key={side}
                className={`survey-step survey-step--${state}`}
              >
                <span className="survey-step__icon">
                  {state === "completed" ? <CheckIcon /> : <CircleIcon />}
                </span>
                <span className="survey-step__label">
                  {SIDE_LABELS[side]}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Validation error */}
      {validationError && (
        <div className="survey-panel__error">{validationError}</div>
      )}
    </div>
  );
};
